/**
 * Wraps navigator.modelContext.registerTool and silently ignores duplicate
 * registrations (InvalidStateError). These can occur during HMR / Fast Refresh
 * in development when componentDidMount or useEffect fires more than once while
 * the browser's ModelContext already holds the tool from the previous mount.
 * Any other error is re-thrown.
 */
export const hasModelContext = () =>
  typeof navigator !== 'undefined' && typeof navigator.modelContext?.registerTool === 'function';

export const safeRegisterTool = tool => {
  if (!hasModelContext()) return;

  try {
    navigator.modelContext.registerTool(tool);
  } catch (e) {
    if (e.name !== 'InvalidStateError') throw e;
  }
};

/**
 * Polls the DOM until `selector` matches an element or `timeout` ms elapses.
 */
export const waitFor = (selector, timeout = 15000) =>
  new Promise((resolve, reject) => {
    const deadline = Date.now() + timeout;
    const poll = () => {
      const el = document.querySelector(selector);
      if (el) {return resolve(el);}
      if (Date.now() > deadline) {
        return reject(new Error(`WebMCP: element not found: ${selector}`));
      }
      setTimeout(poll, 100);
    };
    poll();
  });


/**
 * Sets a value on a plain HTML input and fires input + change events.
 * Use for non-React forms (e.g. HubSpot embeds).
 */
export const fillField = (selector, value) => {
  const el = document.querySelector(selector);
  if (!el) throw new Error(`WebMCP: field not found: ${selector}`);
  el.focus();
  // eslint-disable-next-line no-param-reassign
  el.value = value;
  el.dispatchEvent(new Event('input', {bubbles: true}));
  el.dispatchEvent(new Event('change', {bubbles: true}));
  el.blur();
};

/**
 * Sets a value on a React-controlled <input> by calling the native prototype
 * setter, which bypasses React's closure and properly triggers onChange.
 */
export const setReactInputValue = (el, value) => {
  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLInputElement.prototype,
    'value'
  ).set;
  nativeSetter.call(el, value);
  el.dispatchEvent(new Event('input', {bubbles: true}));
  el.dispatchEvent(new Event('change', {bubbles: true}));
};

/**
 * Sets a value on a React-controlled <select> by calling the native prototype
 * setter, then fires a change event.
 */
export const setReactSelectValue = (el, value) => {
  const nativeSetter = Object.getOwnPropertyDescriptor(
    window.HTMLSelectElement.prototype,
    'value'
  ).set;
  nativeSetter.call(el, value);
  el.dispatchEvent(new Event('change', {bubbles: true}));
};
